"use client";

import { CountdownTimer } from "@/components/common/countdown-timer";
import { Clock4Icon, ListChecksIcon, PlayIcon } from "lucide-react";
import { useContext, useEffect, useRef, useState } from "react";
import { getTimeForExam } from "../exam-question/components/exam-timer/manual-exam-timer/service";
import { Button } from "@/components/ui/button";
import { clientPost } from "@/service/client-api/client-api";
import { createPath } from "@/service/routing/routing";
import { useRouter } from "@bprogress/next";
import { ResourceContext } from "@/contexts/resource-context";
import { SuperLink } from "@/components/common/super-link";
import { logEvent } from "@/service/logs/log";

export const PracticeExamCountdown = ({
  practiceExamStatus,
}: {
  practiceExamStatus: ExamPracticeExamStatusVM;
}) => {
  const { startDate, endDate, isFinishedByUser } = practiceExamStatus;
  const router = useRouter();
  const { logView } = useContext(ResourceContext);

  const lastRefreshRef = useRef(0);
  const REFRESH_COOLDOWN = 10_000;

  const isActive =
    startDate &&
    endDate &&
    new Date() >= new Date(startDate) &&
    new Date() <= new Date(endDate);

  const [timeLeft, setTimeLeft] = useState(
    getTimeForExam(new Date((isActive ? endDate : startDate) || new Date())),
  );

  useEffect(() => {
    if (!startDate || !endDate) return;

    const updateTimeLeft = () => {
      const timeLeft = getTimeForExam(
        new Date((isActive ? endDate : startDate) || new Date()),
      );

      const totalSecondsLeft =
        (timeLeft.days ?? 0) * 86400 +
        timeLeft.hours * 3600 +
        timeLeft.minutes * 60 +
        timeLeft.seconds;

      if (totalSecondsLeft <= 0) {
        const now = Date.now();

        if (now - lastRefreshRef.current > REFRESH_COOLDOWN) {
          lastRefreshRef.current = now;
          router.refresh();
        }

        return;
      }

      setTimeLeft(timeLeft);
    };

    const intervalId = setInterval(updateTimeLeft, 1000);

    return () => clearInterval(intervalId);
  }, [startDate]);

  async function createPracticeExam() {
    const { id } = await clientPost("exam.start_practice_attempt");

    window.location.replace(
      createPath("exam.attempt.question", {
        attempt: id,
        questionNumber: 1,
      }),
    );

    logEvent("practice_exam_cta_start_clicked", logView);
  }

  function onCheckResults() {
    router.push(createPath("exam.practice_exam"));
  }

  const buttons = {
    start: (
      <Button
        variant={"blue"}
        onClick={createPracticeExam}
        disabled={!isActive}
        className="relative z-20 mt-4 w-full md:mt-0 md:w-fit"
      >
        <PlayIcon size={16} className={"stroke-white"} /> Rozpocznij
      </Button>
    ),
    checkResults: (
      // Aktywne także w trakcie rundy — po oddaniu podejścia przejście do rankingu ma sens od razu
      <Button
        variant={"blue"}
        onClick={onCheckResults}
        className="relative z-20 mt-4 w-full md:mt-0 md:w-fit"
      >
        <ListChecksIcon size={16} className={"stroke-white"} /> Sprawdź wyniki
      </Button>
    ),
  };

  return (
    <div className="relative">
      <SuperLink
        href={createPath("exam.practice_exam")}
        className="absolute inset-0 z-10 h-full w-full opacity-0"
      />
      <div className="flex h-full w-full flex-col items-center gap-4 rounded-[10px] border-[1.20px] border-blue-700 bg-blue-600/5 py-4 pr-2 pl-4 md:min-h-14 md:flex-row">
        <span className="flex items-center gap-2 text-sm">
          <Clock4Icon size={20} className="text-blue-700" /> Próbny LEK z
          Remedium!
        </span>
        <CountdownTimer timeLeft={timeLeft} isActive={!!isActive} />
        {buttons[isFinishedByUser ? "checkResults" : "start"]}
      </div>
    </div>
  );
};
